// Logs event to either console or server based on the environment.

import pino, { Logger, LogEvent } from 'pino';
import CommonApi from '../api/CommonApi';
const hostname = window && window.location && window.location.hostname;

/** Receives error-level logs so they can be forwarded to an external monitor. */
type ErrorSink = (error: unknown) => void;

// Registered by other layers so this module stays a leaf (no RUM/auth imports).
let errorSink: ErrorSink | undefined;

/** Registers a sink that receives every error-level log (e.g. to report it to CloudWatch RUM). */
export function setErrorSink(sink: ErrorSink): void {
  errorSink = sink;
}

/** Pulls the error payload out of a pino error log, or undefined when there is none. */
function extractError(logEvent: LogEvent): unknown {
  const first = logEvent.messages[0];
  if (!first || typeof first !== 'object') return undefined;

  // A serialized top-level Error carries pino's msg field — report it as-is.
  if ('msg' in first) return first;

  // Otherwise report a nested { err } / { error } error object.
  const wrapped = first.err ?? first.error;
  if (wrapped && typeof wrapped === 'object') return wrapped;

  return undefined;
}

/**
 * Pino's custom `browser.transmit` handler, allowing us to send the error to a
 * custom destination (CloudWatch RUM/ errorSink in this situation)
 */
const transmit = {
  level: 'error',
  send: (_level: string, logEvent: LogEvent): void => {
    try {
      // Only forward real errors; skip logs that carried no Error to report.
      const error = extractError(logEvent);
      if (errorSink && error) {
        errorSink(error);
      }
    } catch (sinkError) {
      // A logger must never break its caller, but surface the sink failure to the console.
      console.warn('Error sink threw while reporting a log event', sinkError);
    }
  },
};

let logLevel = 'error';
let browser = {};
// Set logger to log in Cloud watch for Gamma and Prod environment
if (
  hostname === 'gamma.apg-library.proserve.aws.dev' ||
  hostname === 'mpl-proserve.amazonaws.com' ||
  hostname === 'apg-library.amazonaws.com'
) {
  logLevel = 'info';
  browser = {
    // @ts-expect-error TS7006 - strict migration
    write: (o) => {
      if (!o.hash) {
        CommonApi.sendLogsToServer(o);
      }
    },
    transmit,
  };
} else {
  // Set logger to log in console
  logLevel = 'debug';
  browser = {
    asObject: false,
    transmit,
  };
}

const log = pino({
  browser,
  level: logLevel,
});

/**
 * @deprecated Use `log` instead. localLog is console-only — it is never
 * transmitted to the server or reported to CloudWatch RUM.
 */
const localLog: Logger = pino({
  browser: { asObject: false },
  level: logLevel,
});

// @ts-expect-error TS7006 - strict migration
function createLoggerObject(lg) {
  return {
    // @ts-expect-error TS7019 - strict migration
    trace: (...args) => lg.trace(...args),
    // tslint:disable-next-line:object-literal-sort-keys
    // @ts-expect-error TS7019 - strict migration
    debug: (...args) => lg.debug(...args),
    // @ts-expect-error TS7019 - strict migration
    info: (...args) => lg.info(...args),
    // @ts-expect-error TS7019 - strict migration
    warn: (...args) => lg.warn(...args),
    // @ts-expect-error TS7019 - strict migration
    error: (...args) => lg.error(...args),
    // @ts-expect-error TS7019 - strict migration
    fatal: (...args) => lg.fatal(...args),
  };
}

export { localLog, log };
